<template>
  <div :class="['relative', sizeClass]">
    <div class="logo" />
    <div class="logo-text" />
  </div>
</template>

<script lang="ts" setup>
const props = defineProps({
  size: {
    type: String,
    default: "large",
  },
});

const sizeClass = computed(() => {
  if (props.size === "small") {
    return "w-16 h-16";
  }

  if (props.size === "medium") {
    return "w-24 h-24";
  }

  return "w-32 h-32";
});
</script>

<style scoped>
.logo {
  --r1: 154%;
  --r2: 68.5%;
  --loader-primary: var(--p-primary-color, var(--p-primary-900, #172033));
  --loader-primary-soft: var(--p-primary-100, #e8e9eb);
  width: 100%;
  height: 100%;
  overflow: hidden;
  -webkit-mask: url('~/assets/icons/ocv-logo-vert.svg') center / contain no-repeat;
  mask: url('~/assets/icons/ocv-logo-vert.svg') center / contain no-repeat;
  position: absolute;
  top: 0;
  left: 0;
}

/*
 * The wave moves with `transform`, which the compositor animates without
 * repainting the page. Animating `background-position` repainted every frame.
 * The layer holds 11 wave tiles (each 50.5% of the logo wide, 220% tall),
 * starts 8 tiles to the left, and moves like the old background did.
 */
.logo::before {
  content: "";
  position: absolute;
  top: 0;
  left: -404%;
  width: 555.5%;
  height: 220%;
  background:
    radial-gradient(var(--r1) var(--r2) at bottom, var(--loader-primary) 79.5%, #0000 80%),
    var(--loader-primary-soft);
  background-size: calc(100% / 11) 100%;
  background-repeat: repeat-x;
  will-change: transform;
  animation: logoWave 4s infinite linear;
}

.logo-text {
  width: 100%;
  height: 100%;
  background: var(--p-primary-inverse, #ffffff);
  -webkit-mask: url('~/assets/icons/ocv-logo-text.svg') center / contain no-repeat;
  mask: url('~/assets/icons/ocv-logo-text.svg') center / contain no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1; /* Above the animated logo */
}

/*
 * Same path as the old `background-position` -100% 0% → 400% 100% → 800% 0%.
 * One background-position step of 100% moves the wave 49.5% of the logo,
 * so x = p × 49.5% / 555.5% of the layer; y = -120% / 220% of the layer.
 */
@keyframes logoWave {
  0% { transform: translate(-8.911%, 0) }
  50% { transform: translate(35.644%, -54.545%) }
  100% { transform: translate(71.287%, 0) }
}

/* Reduced motion: no wave, show the full logo. */
@media (prefers-reduced-motion: reduce) {
  .logo::before {
    animation: none;
    transform: translate(35.644%, -54.545%);
  }
}
</style>
